'use client';

import { useRef, type DependencyList } from 'react';

import { useIsomorphicLayoutEffect } from '@/react/hooks/useIsomorphicLayoutEffect';
import { GlobalCSSCustomProperty } from '@/type-utils/css';
import { isNullish } from '@/utils/null-utils';

/**
 * Sets a CSS custom property on the document root element (`:root`).
 *
 * This is useful for "lifting" component-scoped CSS custom properties to the global
 * context, making them accessible anywhere in the CSS cascade.
 *
 * The property is automatically removed when the component unmounts, or when `value` is set to `null`. Alternatively,
 * the property can be restored to its initial value when the component unmounts.
 *
 * ## Why `GlobalCSSCustomProperty`?
 *
 * This hook restricts property names to the {@link GlobalCSSCustomProperty} enum rather than
 * accepting arbitrary strings. This design choice is intentional for several reasons:
 *
 * ### 1. Centralized Control & Discoverability
 * Global CSS custom properties affect the entire application's styling. By defining them in a
 * single enum, we create a single source of truth that makes it easy to:
 * - Discover which global properties exist
 * - Understand their purpose through documentation
 * - Track where they're defined and used
 *
 * ### 2. Preventing Naming Conflicts
 * Without restrictions, any component could set any `--property-name` on `:root`, leading to:
 * - Accidental overwrites of properties set by other components
 * - Unpredictable styling bugs that are difficult to trace
 * - Race conditions when multiple components try to control the same property
 *
 * ### 3. Maintaining CSS Architecture Integrity
 * Global properties should correspond to well-defined SCSS variables in the design system
 * (e.g., `$header-height`). Allowing arbitrary properties would bypass this architecture and
 * create an uncontrolled parallel system of global styles.
 *
 * ### 4. Type Safety & Refactoring
 * The enum provides compile-time checking, making it impossible to typo a property name and
 * enabling safe refactoring when property names need to change.
 *
 * If you need a new global CSS custom property, add it to the {@link GlobalCSSCustomProperty}
 * enum with appropriate documentation explaining its purpose and corresponding SCSS variable.
 *
 * @param propertyName - A value from the {@link GlobalCSSCustomProperty} enum.
 * @param value - The value to set for the property (e.g., `'40px'`, `'red'`).
 *   Pass `null` to remove the property.
 * @param deps - Optional dependency array to control when the property syncs.
 *   If omitted, syncs whenever `propertyName` or `value` changes.
 *
 * @example
 * // Basic usage - syncs the header height to :root
 * const headerHeight = headerRef.current?.offsetHeight ?? 0;
 * useGlobalCSSCustomProperty(GlobalCSSCustomProperty.HeaderUtilityBarHeight, `${headerHeight}px`);
 *
 * @example
 * // With dependency array - only syncs when scrollY changes
 * useGlobalCSSCustomProperty(
 *   GlobalCSSCustomProperty.HeaderUtilityBarHeight,
 *   `${calculatedHeight}px`,
 *   [scrollY, calculatedHeight]
 * );
 *
 * @example
 * // Conditionally remove the property
 * useGlobalCSSCustomProperty(GlobalCSSCustomProperty.ModalOffset, isOpen ? '100px' : null);
 */
export function useGlobalCSSCustomProperty(
  propertyName: GlobalCSSCustomProperty,
  value: string | null,
  deps: DependencyList = []
): void {
  // Store the initial value to restore on unmount
  const initialPropertyValue = useRef<string | null>(null);
  // Store the current value to avoid unnecessary DOM updates
  const currentValue = useRef<string | null>(null);

  useIsomorphicLayoutEffect(() => {
    const root = document.documentElement;

    if (!isNullish(value)) {
      // get the initial value only once
      if (isNullish(initialPropertyValue.current)) {
        const existingValue = root.style.getPropertyValue(propertyName);
        initialPropertyValue.current = existingValue || null;
      }
      root.style.setProperty(propertyName, value);
      currentValue.current = value;
    } else if (!isNullish(currentValue.current)) {
      // Remove the property if it was previously set by this hook
      root.style.removeProperty(propertyName);
      currentValue.current = null;
    }

    return () => {
      if (!isNullish(initialPropertyValue.current)) {
        // Restore the initial value
        root.style.setProperty(propertyName, initialPropertyValue.current);
      } else {
        // Remove the property if there was no initial value
        root.style.removeProperty(propertyName);
      }
      currentValue.current = null;
    };
  }, [propertyName, value, ...deps]);
}
